import type { DiaryDayDocType } from '../validators';
import type { Assert, AssertEqual } from './type-assertions';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';
import {
  defineRxdbCollectionModel,
  defineRxdbMigration,
  defineRxdbMigrations,
  toRxdbMigrationStrategies,
} from './migration';
import { SERVER_MODIFIER_ID } from '../validators';
import { migrateDiaryDayBodyV1ToV2 } from './migrations/diary-day';

// ============================================================================
// RxDB collection schema for diaryDays.
//
// Document shape mirrors the D1 `diary_days` row. `body` is opaque JSON.
// `_deleted` is supplied by RxDB at runtime; it is NOT part of the persisted
// document type — server replies set it from `deletedAt !== null`.
// ============================================================================

export const DIARY_DAYS_COLLECTION = 'diaryDays' as const;
// v2: body refactored to a meals-centered shape (see migrateDiaryDayBodyV1ToV2).
export const DIARY_DAYS_SCHEMA_VERSION = 2;

type DiaryDayDocTypeV0 = Omit<DiaryDayDocType, 'syncRevision' | 'modifiedBy'>;

export const diaryDaysSchemaLiteral = {
  title: 'diary day schema',
  version: DIARY_DAYS_SCHEMA_VERSION,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    userId: { type: 'string', maxLength: 100 },
    date: { type: 'string', maxLength: 10 },
    body: { type: 'object' },
    createdAt: { type: 'string', maxLength: 30 },
    updatedAt: { type: 'string', maxLength: 30 },
    deletedAt: { type: ['string', 'null'] },
    syncRevision: { type: 'number', minimum: 0, multipleOf: 1 },
    modifiedBy: { type: 'string', maxLength: 100 },
  },
  required: [
    'id',
    'userId',
    'date',
    'body',
    'createdAt',
    'updatedAt',
    'deletedAt',
    'syncRevision',
    'modifiedBy',
  ],
  indexes: [['userId', 'updatedAt']],
} as const;

export interface DiaryDaysCheckpoint {
  sequence: number;
}

type _DiaryDaySchemaDocType = Omit<ExtractDocumentTypeFromTypedRxJsonSchema<typeof diaryDaysSchemaLiteral>, 'body'> & {
  body: DiaryDayDocType['body'];
};
type _DiaryDayDocTypeInSync = Assert<AssertEqual<_DiaryDaySchemaDocType, DiaryDayDocType>>;

export const diaryDaySchema: RxJsonSchema<DiaryDayDocType> = diaryDaysSchemaLiteral;
export const diaryDayMigrationSteps = defineRxdbMigrations(
  defineRxdbMigration<DiaryDayDocTypeV0, DiaryDayDocType, 1>(
    1,
    (old) => {
      console.log('abc-1');
      return {
        ...old,
        syncRevision: 0,
        modifiedBy: SERVER_MODIFIER_ID,
      };
    },
  ),
  // v1 -> v2: convert the legacy scattered body (mealPlans + entries +
  // planOverride + periodization overrides/locks) into the meals-centered body.
  defineRxdbMigration<DiaryDayDocType, DiaryDayDocType, 2>(
    2,
    (old) => {
      console.log('abc-2');
      return {
      ...old,
      body: migrateDiaryDayBodyV1ToV2(old.body),
    }; },
  ),
);
export const diaryDayMigrations = toRxdbMigrationStrategies(diaryDayMigrationSteps);
export const diaryDaysModel = defineRxdbCollectionModel<typeof DIARY_DAYS_COLLECTION, DiaryDayDocType>({
  collection: DIARY_DAYS_COLLECTION,
  schema: diaryDaySchema,
  migrationStrategies: diaryDayMigrations,
});
